import * as React from 'react';
import {useSelector} from 'react-redux';

import {formValueSelector} from 'redux-form';

import {StackLegacy} from 'shells/layout/stack/stack-legacy';

import type {EmailableLead} from 'nutshell-graphql-types';

import type {NutshellState} from '../../../../store';

import {getMaterializationEntityIdFromToAddresses} from '../helpers';
import {CREATE_FROM_SCRATCH_ID, FORM_NAME} from '../types';
import {useGetMaterializedSequenceTemplate} from '../use-get-materialized-sequence-template';

import {FromScratchButton} from './from-scratch-button';
import {GroupsList} from './groups-list';
import {SearchBar} from './search-bar';

import styles from './template-sidebar.module.css';

const valueSelector = formValueSelector(FORM_NAME);

type Props = {
    isLoading: boolean;
    selectedSequenceId: string;
    selectedTemplateId: string;
    onTemplateSelect: (selectedSequenceId: string, selectedTemplateId: string) => void;
    onCreateFromScratch: () => void;
    closeModal: () => void;
    searchValue: string;
    setSearchValue: (searchValue: string) => void;
    relatedLeads: EmailableLead[];
    replyQuotedSuffixHtml?: string | null;
};

export function TemplateSidebar(props: Props) {
    const [materializeTemplateId, setMaterializeTemplateId] = React.useState<
        string | null | undefined
    >(null);
    const [materializedTemplate, setMaterializedTemplate] = React.useState<any | null | undefined>(
        null
    );

    const toAddressesValue = useSelector((state: NutshellState) =>
        valueSelector(state, 'toAddresses')
    );

    const materializationEntityId = getMaterializationEntityIdFromToAddresses(
        toAddressesValue,
        props.relatedLeads[0]?.id
    );

    // When a sequence is clicked, we set the materializeTemplateId then fetch that materialized template
    const {materializedSequenceTemplate, isMaterializing} = useGetMaterializedSequenceTemplate(
        materializeTemplateId,
        materializationEntityId
    );

    // Once we have the materializedSequenceTemplate, we set it into state and pass it to the groups list,
    // where we update the form state to include this new info on the selected sequence
    if (
        materializedSequenceTemplate &&
        (!materializedTemplate ||
            materializedTemplate.emailSequenceTemplate.id !==
                materializedSequenceTemplate.emailSequenceTemplate.id)
    ) {
        setMaterializedTemplate(materializedSequenceTemplate);
    }

    return (
        <div className={styles.container}>
            <StackLegacy spacing={16}>
                <SearchBar
                    value={props.searchValue}
                    onChange={(val: string) => props.setSearchValue(val)}
                />
                <FromScratchButton
                    isActive={props.selectedSequenceId === CREATE_FROM_SCRATCH_ID}
                    onClick={props.onCreateFromScratch}
                />
                <GroupsList
                    {...props}
                    setMaterializeTemplateId={setMaterializeTemplateId}
                    materializedSequenceTemplate={materializedTemplate}
                    isMaterializing={isMaterializing}
                    searchValue={props.searchValue}
                    setSearchValue={props.setSearchValue}
                />
            </StackLegacy>
        </div>
    );
}
